import CircleWarningIcon from '@assets/icons/circle-warning.svg';
import i18n from '@/utils/i18n';
import LockIcon from '@assets/icons/lock.svg';
import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  applyEditToRow,
  CostChangeValue,
  PricingAccountRow,
  usePricingAccountRowsTransformer,
  useVariantPackCostChangeAggregateTransformer,
} from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/transformers/use-pricing-management-transformers';
import { applyPricingFilters } from '@sales-portal/domain/pricing/blocs/utils/pricing-filters';
import { AppPageLayout } from '@experiences/templates/app-page-layout';
import {
  BAR_CONTROL_HEIGHT,
  PricingBulkEditBottomBar,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-bulk-edit-bottom-bar';
import {
  bookmarkKey,
  usePricingBookmarksStore,
} from '@sales-portal/domain/pricing/blocs/stores/pricing-bookmarks-store';
import {
  BookmarkPair,
  useBookmarkActionForPairs,
} from '@sales-portal/domain/pricing/blocs/hooks/use-bookmark-action';
import { buildPricingFilterSections } from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-sections';
import {
  BulkEditPivot,
  useBulkEditingCore,
} from '@sales-portal/domain/pricing/blocs/hooks/use-bulk-editing-core';
import {
  Button,
  ButtonSize,
  ButtonVariant,
} from '@/experiences/sales-portal/components/button';
import {
  CellContext,
  ColumnDef,
  createColumnHelper,
  SortingState,
} from '@tanstack/react-table';
import { Checkbox, Tooltip } from '@usepepper/sprout-kit-ui';
import {
  COLUMN_WIDTH,
  PRICING_CONTENT_MIN_WIDTH,
  PRICING_FILTER_PANEL_WIDTH,
  PRICING_TABLE_LAYOUT,
} from '@sales-portal/domain/pricing/constants';
import {
  ColumnId,
  PricingItemManagementTableRow,
} from '@/experiences/sales-portal/domain/pricing/screens/management/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import { ConfigureColumnsButton } from '@experiences/components/table/configure-columns-button';
import { createStyles } from '@/contexts/theme-context';
import {
  emptyDelta,
  formatOrEmpty,
  isEmptyDisplayValue,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/missing-value-display';
import { EmptyState } from '@experiences/components/empty-state';
import {
  formatCurrency,
  formatPriceInMicrosWithoutSymbol,
  getCurrencySymbol,
  MICRO_CONVERSION,
  roundMicrosToNearestWholeCent,
} from '@/utils/money';
import { formatPercentage } from '@sales-portal/blocs/utils/money';
import {
  getHeaderLabel,
  PricingTableHeader,
} from '@sales-portal/domain/pricing/screens/management/components/pricing-table-header';
import {
  getLastCostInMicros,
  getLastPriceInMicros,
  percentOfPrevious,
  roundPercentageToNearestTenth,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/variant-pack-cost-change-pricing';
import { getTranslationValue } from '@/utils/translations';
import {
  IMPACT_COLUMN_CONFIGS,
  isDeltaColumn,
  isLeftAlignedColumn,
  pricingItemGlobalFilterFn,
  resolveDelta,
} from '@sales-portal/domain/pricing/screens/management/blocs/utils/table-ui';
import { ItemInputCell } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-input-cell';
import { ItemUpdatedPricingCellContainer } from '@sales-portal/domain/pricing/screens/management/blocs/containers/item-updated-pricing-cell-container';
import { LayoutChangeEvent, View } from 'react-native';
import { PricingAggregateRow } from '@/experiences/sales-portal/domain/pricing/components/pricing-aggregate-row';
import {
  PricingAnalyticEvent,
  pricingManagementAnalytics,
  PricingPageSource,
} from '@/experiences/sales-portal/domain/pricing/analytics/pricing-management-analytics';
import { PricingBreadcrumbs } from '@sales-portal/domain/pricing/components/pricing-breadcrumbs';
import {
  PricingEdit,
  PricingEditField,
} from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store-types';
import { PricingFilterButton } from '@sales-portal/domain/pricing/components/pricing-filter-button';
import { PricingFilterEmptyState } from '@sales-portal/domain/pricing/components/pricing-filter-empty-state';
import { PricingFilterPanel } from '@sales-portal/domain/pricing/components/pricing-filter-panel';
import {
  PricingFilterScope,
  PricingFilterUnit,
  PricingRangeColumnConfig,
} from '@sales-portal/domain/pricing/blocs/utils/pricing-filter-types';
import { PricingItemDetailsTableRow } from '@sales-portal/domain/pricing/screens/management/components/pricing-item-details-table-row';
import { PricingPeerRangeCell } from '@sales-portal/domain/pricing/screens/management/blocs/containers/pricing-peer-range-cell';
import { PricingReviewButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-review-button';
import { PricingSearchBar } from '@sales-portal/domain/pricing/components/pricing-search-bar';
import { PricingSelectAllRow } from '@sales-portal/domain/pricing/screens/management/components/pricing-select-all-row';
import { PricingTableDeltaCell } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-delta-cell';
import { PricingTableEmptyComponent } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-empty-component';
import { PricingTableHeaderCells } from '@sales-portal/domain/pricing/components/pricing-table-header-cells';
import { PricingTableNameLink } from '@sales-portal/domain/pricing/components/pricing-table-name-link';
import { PricingTableSkeleton } from '@/experiences/sales-portal/domain/pricing/screens/management/components/pricing-table-skeleton';
import { PricingUndoButton } from '@sales-portal/domain/pricing/screens/management/components/pricing-undo-button';
import {
  RenderHeaderRowCallback,
  RenderRowCallback,
} from '@experiences/components/table/types';
import { SALES_PORTAL_ROUTES } from '@/navigators/routes';
import { SalesHubShellTemplate } from '@/experiences/sales-portal/templates/sales-hub-shell-template';
import {
  SalesPortalKeyboardScope,
  SalesPortalShortcuts,
} from '@/experiences/sales-portal/platform/shortcuts/sales-portal-shortcuts';
import { SalesPortalStackParams } from '@/navigators/types';
import {
  scopeStorageKey,
  selectVariantPackEditsCount,
  usePricingEditsStore,
  variantPackScope,
} from '@/experiences/sales-portal/domain/pricing/blocs/stores/pricing-edits-store';
import {
  SearchInput,
  SearchInputRef,
} from '@/experiences/sales-portal/components/search-input';
import { SearchSkeleton } from '@/experiences/sales-portal/components/search-skeleton';
import { StackScreenProps } from '@react-navigation/stack';
import { Table } from '@experiences/components/table/table';
import { Typography } from '@usepepper/ui/atoms/typography';
import { useBulkQuickActions } from '@sales-portal/domain/pricing/blocs/hooks/use-bulk-quick-actions';
import { useBusinessOrganizationPricingConfig } from '@/experiences/sales-portal/blocs/hooks/use-business-organization-pricing-config';
import { useConfigureColumns } from '@experiences/components/table/blocs/hooks/use-configure-columns';
import { useFocusedPricingEditsStore } from '@sales-portal/domain/pricing/blocs/hooks/use-focused-pricing-edits-store';
import { useFocusEffect } from '@react-navigation/native';
import { usePricingEditsTimezoneSync } from '@sales-portal/domain/pricing/screens/management/blocs/hooks/use-pricing-edits-timezone-sync';
import { usePricingFilterPanel } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filter-panel';
import { usePricingFilters } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters';
import { usePricingFiltersUrlSync } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-filters-url-sync';
import { usePricingSearch } from '@/experiences/sales-portal/domain/pricing/blocs/hooks/use-pricing-search';
import { usePricingSortSnapshot } from '@sales-portal/domain/pricing/blocs/hooks/use-pricing-sort-snapshot';
import { useRowSelection } from '@sales-portal/domain/pricing/blocs/hooks/use-row-selection';
import { useScreenScopedKeyboardShortcuts } from '@experiences/platform/shortcuts/use-screen-scoped-keyboard-shortcuts';
import { useVariantPackDetailedCostChangeDataLoader } from '@/experiences/sales-portal/domain/pricing/screens/management/blocs/loaders/variant-pack-detailed-cost-change-data-loader';
import { WeeklyPurchaseQuantitiesCell } from '@sales-portal/domain/pricing/screens/management/components/weekly-purchase-quantities-cell';

const columnHelper = createColumnHelper<PricingItemManagementTableRow>();

const getRowId = (row: PricingItemManagementTableRow): string =>
  (row as PricingAccountRow).restaurantUUID;

const isRowLocked = (row: PricingItemManagementTableRow): boolean =>
  Boolean((row as PricingAccountRow).isLocked);

type Props = StackScreenProps<
  SalesPortalStackParams,
  typeof SALES_PORTAL_ROUTES.PricingItemManagement
>;

type PricingCellRenderInput = CellContext<
  PricingItemManagementTableRow,
  string
> & {
  /** Rendered row index, injected by the row component. */
  index?: number;
  isAggregateRow?: boolean;
};

const accountRowKey = (row: PricingAccountRow): string => row.restaurantUUID;

export const PricingItemManagementScreen: React.FC<Props> = (props) => {
  const { navigation, route } = props;
  const { supplierUUID, variantPackUUID } = route.params;
  const {
    hasBookmarkingAccess,
    isHistoricalPricingContextEnabled,
    isPeerRangeEnabled,
    costChangeLookbackMode,
    costChangeWeekAnchorDay,
  } = useBusinessOrganizationPricingConfig();
  const [styles, theme] = useStyles();
  const [tableLayoutWidth, setTableLayoutWidth] = useState<number | null>(null);
  const handleTableLayout = useCallback(
    (event: LayoutChangeEvent) =>
      setTableLayoutWidth(event.nativeEvent.layout.width),
    [],
  );
  const { clearSearch, debouncedSearchText, searchText, setSearchText } =
    usePricingSearch();
  const [sorting, setSorting] = useState<SortingState>([
    { desc: false, id: ColumnId.WeeklyNetGrossProfitImpact },
  ]);
  const { currencyCode, timezone, timezoneLoading } =
    usePricingEditsTimezoneSync();

  const hasPriceEdits = useFocusedPricingEditsStore(
    (state) => state.edits.length > 0,
  );
  const editScope = useMemo(
    () => variantPackScope(variantPackUUID),
    [variantPackUUID],
  );
  const hasUndoHistory = useFocusedPricingEditsStore(
    (state) =>
      (state.editSnapshotStack[scopeStorageKey(editScope)]?.length ?? 0) > 0,
  );
  const pricingEdits = useFocusedPricingEditsStore((state) => state.edits);
  const activeEditsByVariantPackCount = useFocusedPricingEditsStore((state) =>
    selectVariantPackEditsCount(state, variantPackUUID),
  );
  const { data, loading, error, refetch } =
    useVariantPackDetailedCostChangeDataLoader(supplierUUID, variantPackUUID);

  const undoLastEdit = usePricingEditsStore((state) => state.undo);
  const resetUndoHistory = usePricingEditsStore(
    (state) => state.resetUndoHistory,
  );

  const handleUndo = useCallback(() => {
    undoLastEdit(editScope);
    pricingManagementAnalytics.track(PricingAnalyticEvent.UNDO, {
      pageSource: PricingPageSource.ITEM_DETAILS,
    });
  }, [editScope, undoLastEdit]);

  const hasTrackedLoad = useRef(false);
  useEffect(() => {
    if (!loading && !error && data && !hasTrackedLoad.current) {
      hasTrackedLoad.current = true;
      pricingManagementAnalytics.track(
        PricingAnalyticEvent.LOAD_COST_CHANGES_ITEM_DETAILS,
        {},
      );
    }
  }, [loading, error, data]);

  const dataToTransform = useMemo(
    () => data?.getVariantPackDetailedCostChangeData ?? [],
    [data?.getVariantPackDetailedCostChangeData],
  );

  const filteredPriceEditMap = useMemo(
    () =>
      pricingEdits.reduce<Record<string, PricingEdit>>((acc, edit) => {
        if (edit.variantPackUUID === variantPackUUID) {
          acc[edit.restaurantUUID] = edit;
        }
        return acc;
      }, {}),
    [pricingEdits, variantPackUUID],
  );

  const transformedData = usePricingAccountRowsTransformer(dataToTransform);

  const priceFloorEnabled = transformedData[0]?.priceFloorEnabled ?? false;
  const priceFloorMode = transformedData[0]?.priceFloorMode ?? null;

  const mergedData = useMemo(() => {
    return transformedData.map((row) => {
      const edit = filteredPriceEditMap[row.restaurantUUID];
      return edit ? applyEditToRow(row, edit) : row;
    });
  }, [filteredPriceEditMap, transformedData]);

  const { onSortingChange, resolveEditedRow } = usePricingSortSnapshot({
    keyOf: accountRowKey,
    rows: mergedData,
    setSorting,
    source: data,
  });

  const {
    clearSelection,
    filteredCount,
    isAllLocked,
    isAllSelected,
    isPartiallySelected,
    onFilteredRowsChange,
    selectedCount,
    selectedUUIDs: selectedAccountUUIDs,
    toggleAllVisible,
    toggleSelection: toggleAccountSelection,
  } = useRowSelection<PricingItemManagementTableRow>({
    getRowId,
    isLocked: isRowLocked,
  });

  useFocusEffect(
    useCallback(() => {
      resetUndoHistory(editScope);
      clearSelection();
      setSearchText('');
    }, [clearSelection, editScope, resetUndoHistory, setSearchText]),
  );

  const {
    applyBulkInput: handleApplyBulkInput,
    bulkEditField,
    bulkEditMode,
    bulkInputValue,
    bulkQuickActionStates,
    canApplyBulkInput,
    setBulkEditField,
    setBulkEditMode,
    setBulkInputValue,
  } = useBulkEditingCore({
    currencyCode,
    mergedRows: mergedData,
    pivot: BulkEditPivot.VariantPack,
    pivotUUID: variantPackUUID,
    priceFloorEnabled,
    priceFloorMode,
    pricingEdits,
    rows: transformedData,
    scope: editScope,
    selectedKeys: selectedAccountUUIDs,
  });

  const selectedPairs = useMemo<BookmarkPair[]>(
    () =>
      [...selectedAccountUUIDs].map((accountUUID) => ({
        restaurant_id: accountUUID,
        variant_pack_id: variantPackUUID,
      })),
    [selectedAccountUUIDs, variantPackUUID],
  );
  const bookmarkSupplierUUIDs = useMemo(
    () => (supplierUUID ? [supplierUUID] : []),
    [supplierUUID],
  );

  const {
    bookmarkAction,
    isNoteOpen: isBookmarkNoteOpen,
    onSaveNote: onSaveBookmarkNote,
  } = useBookmarkActionForPairs({
    canBookmarkItem: hasBookmarkingAccess,
    pageSource: PricingPageSource.ITEM_DETAILS,
    pairs: selectedPairs,
    supplierUUIDs: bookmarkSupplierUUIDs,
  });

  const bulkQuickActions = useBulkQuickActions({
    bookmarkAction,
    bulkQuickActionStates,
  });

  // Accounts whose cost did not move say nothing about the cost change this
  // page is about, so they stay out of the aggregate's averages and volume.
  const transformedAggregatedData =
    useVariantPackCostChangeAggregateTransformer(mergedData, {
      ignoreZeroCostDelta: true,
    });

  const aggregateData = transformedAggregatedData[0];

  const searchInputRef = useRef<SearchInputRef>(null);

  // Keyboard shortcut: / to focus search input
  useScreenScopedKeyboardShortcuts(
    SalesPortalShortcuts.Search,
    () => searchInputRef.current?.focus(),
    [],
    {},
    true,
    SalesPortalKeyboardScope.PRICING_MANAGEMENT,
  );

  const isBulkEditBarVisible = selectedCount > 0 && !timezoneLoading;

  const handleSearchChange = (text: string) => {
    setSearchText(text);
  };

  const priceEditColumnCount =
    dataToTransform[0]?.price_edit_display_headers?.length ?? 0;
  const regularColumnCount =
    PRICING_TABLE_LAYOUT.ITEM_DETAIL_REGULAR_COLUMN_COUNT +
    (isPeerRangeEnabled ? 1 : 0) +
    priceEditColumnCount;
  const remainingPercentage = 100 - PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE;
  const columnWidthPercentage = remainingPercentage / regularColumnCount;

  const columns = useMemo<ColumnDef<PricingItemManagementTableRow, string>[]>(
    () => [
      // Checkbox
      columnHelper.display({
        cell: ({ row }) => {
          const originalRow = row.original as PricingAccountRow;
          if (originalRow.isLocked) {
            return (
              <View style={styles.checkboxContainer}>
                <Tooltip placement="right">
                  <Tooltip.Trigger>
                    <LockIcon
                      height={16}
                      stroke={theme.sk.color.icon.tertiary}
                      width={16}
                    />
                  </Tooltip.Trigger>
                  <Tooltip.Content>
                    <Typography
                      color={theme.sk.color.text.ondark}
                      variant="paragraph-sm-600"
                    >
                      {i18n.t('pricing.table.locked_tooltip')}
                    </Typography>
                  </Tooltip.Content>
                </Tooltip>
              </View>
            );
          }

          const isRowSelected = selectedAccountUUIDs.has(
            originalRow.restaurantUUID,
          );

          return (
            <View style={[styles.checkboxContainer, styles.checkbox]}>
              <Checkbox
                checked={isRowSelected}
                onPress={() =>
                  toggleAccountSelection(originalRow.restaurantUUID)
                }
                size="$sm"
                tabIndex={-1}
                testID={`pricing-select-row-${originalRow.restaurantUUID}${isRowSelected ? '-checked' : ''}`}
              />
            </View>
          );
        },
        header: () => null,
        id: ColumnId.Checkbox,
        meta: {
          hideable: false,
        },
        size: PRICING_TABLE_LAYOUT.CHECKBOX_COLUMN_WIDTH,
      }),
      // Name
      columnHelper.accessor(
        (row) => {
          if ('restaurantName' in row) {
            return row.restaurantName;
          }
          return row.variantPackName;
        },
        {
          cell: (info: PricingCellRenderInput) => {
            if (info.isAggregateRow) {
              return (
                <Typography variant="paragraph-md-600">
                  {info.getValue()}
                </Typography>
              );
            }
            return (
              <PricingTableNameLink
                name={info.getValue()}
                onPress={() =>
                  navigation.navigate(
                    SALES_PORTAL_ROUTES.PricingCustomerDetail,
                    {
                      restaurantUUID: info.row.original.restaurantUUID,
                      supplierUUID: info.row.original.supplierUUID,
                    },
                  )
                }
              />
            );
          },
          header: () => <PricingTableHeader columnId={ColumnId.Name} />,
          id: ColumnId.Name,
          meta: {
            alwaysVisible: true,
            initialPercentWidth: PRICING_TABLE_LAYOUT.NAME_COLUMN_PERCENTAGE,
            label: getHeaderLabel(ColumnId.Name),
          },
          minSize: PRICING_TABLE_LAYOUT.NAME_COLUMN_MIN_WIDTH,
          sortingFn: 'alphanumeric',
        },
      ),
      // ID
      columnHelper.accessor(
        (row) => {
          if ('restaurantAccountId' in row) {
            return row.restaurantAccountId;
          }
          return row.variantPackId;
        },
        {
          cell: (info) => (
            <Typography
              color={theme.sk.color.text.primary}
              variant="paragraph-md"
            >
              {info.getValue()}
            </Typography>
          ),
          header: () => <PricingTableHeader columnId={ColumnId.Id} />,
          id: ColumnId.Id,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.Id),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: 'alphanumeric',
        },
      ),
      // UOM
      columnHelper.accessor((row) => row.variantPackUOM, {
        cell: (info) => (
          <Typography
            color={theme.sk.color.text.primary}
            textTransform="capitalize"
            variant="paragraph-md"
          >
            {info.getValue()}
          </Typography>
        ),
        header: () => <PricingTableHeader columnId={ColumnId.UOM} />,
        id: ColumnId.UOM,
        meta: {
          initialPercentWidth: columnWidthPercentage,
          label: getHeaderLabel(ColumnId.UOM),
        },
        ...COLUMN_WIDTH.M,
        sortingFn: 'alphanumeric',
      }),
      // Peer Range
      ...(isPeerRangeEnabled
        ? [
            columnHelper.display({
              cell: (info) => {
                if ((info as PricingCellRenderInput).isAggregateRow) {
                  return null;
                }
                const originalRow = info.row.original as PricingAccountRow;
                return (
                  <PricingPeerRangeCell
                    grossMarginPercentage={originalRow.grossMarginPercentage}
                    peerRange={originalRow.peerRange}
                    restaurantUUID={originalRow.restaurantUUID}
                    variantPackUUID={variantPackUUID}
                  />
                );
              },
              enableSorting: false,
              header: () => (
                <PricingTableHeader columnId={ColumnId.PeerRange} />
              ),
              id: ColumnId.PeerRange,
              meta: {
                initialPercentWidth: columnWidthPercentage,
                label: getHeaderLabel(ColumnId.PeerRange),
              },
              ...COLUMN_WIDTH.M,
            }),
          ]
        : []),
      // Current GM%
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossMargin], () =>
            formatPercentage(row.grossMarginPercentage, 1, 1),
          ),
        {
          cell: (info: PricingCellRenderInput) => {
            const value = info.getValue();
            if (info.isAggregateRow) {
              return <Typography variant="paragraph-md">{value}</Typography>;
            }

            const originalRow = info.row.original as PricingAccountRow;
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={info.column.id}
                currencyCode={currencyCode}
                disabled={originalRow.isLocked}
                inputType={PricingEditField.GrossMarginPercentage}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatPercentage(
                  info.row.original.grossMarginPercentage,
                  1,
                )}
                originalRow={originalRow}
                priceFloorEnabled={originalRow.priceFloorEnabled}
                priceFloorMode={originalRow.priceFloorMode}
                restaurantUUID={originalRow.restaurantUUID}
                rowIndex={info.row.id}
                scope={editScope}
                tableRowIndex={info.index ?? info.row.index}
                value={String(
                  parseFloat(
                    info.row.original.grossMarginPercentage.toFixed(1),
                  ),
                )}
                variantPackUUID={variantPackUUID}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CurrentGrossMargin}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CurrentGrossMargin,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentGrossMargin),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original as PricingAccountRow);
            const b = resolveEditedRow(rowB.original as PricingAccountRow);
            return a.grossMarginPercentage - b.grossMarginPercentage;
          },
        },
      ),
      // Gross Margin % delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossMarginDelta], () =>
            formatPercentage(
              roundPercentageToNearestTenth(row.grossMarginPercentageDelta),
              1,
              1,
            ),
          ),
        {
          cell: ({ getValue, row, isAggregateRow }: PricingCellRenderInput) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              getValue(),
            )
              ? emptyDelta
              : resolveDelta(
                  roundPercentageToNearestTenth(
                    row.original.grossMarginPercentageDelta,
                  ),
                  (v) => formatPercentage(v, 1, 1),
                );
            if (isAggregateRow) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossMarginPercentageDelta"
                formatValue={(value) =>
                  formatPercentage(roundPercentageToNearestTenth(value), 1, 1)
                }
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-gross-margin-percentage-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossMarginPercentageDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossMarginPercentageDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original as PricingAccountRow);
            const b = resolveEditedRow(rowB.original as PricingAccountRow);
            return a.grossMarginPercentageDelta - b.grossMarginPercentageDelta;
          },
        },
      ),
      // Price
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.Price], () =>
            'restaurantName' in row
              ? formatPriceInMicrosWithoutSymbol(
                  row.priceInMicros,
                  currencyCode,
                )
              : formatCurrency(row.priceInMicros, currencyCode),
          ),
        {
          cell: (info: PricingCellRenderInput) => {
            const value = info.getValue();
            if (info.isAggregateRow) {
              return (
                <Typography
                  color={
                    info.row.index === 0
                      ? theme.sk.color.text.primary
                      : undefined
                  }
                  testID={`price-vp-${variantPackUUID}`}
                  variant="paragraph-md"
                >
                  {value}
                </Typography>
              );
            }
            const priceInMicros = info.row.original.priceInMicros;
            const originalRow = info.row.original as PricingAccountRow;
            return (
              <ItemInputCell
                canBookmarkItem={hasBookmarkingAccess}
                columnId={info.column.id}
                currencyCode={currencyCode}
                disabled={originalRow.isLocked}
                inputType={PricingEditField.Price}
                isHistoricalPricingContextEnabled={
                  isHistoricalPricingContextEnabled
                }
                originalFormattedValue={formatCurrency(
                  priceInMicros,
                  currencyCode,
                )}
                originalRow={originalRow}
                priceFloorEnabled={originalRow.priceFloorEnabled}
                priceFloorMode={originalRow.priceFloorMode}
                restaurantUUID={originalRow.restaurantUUID}
                rowIndex={info.row.id}
                scope={editScope}
                tableRowIndex={info.index ?? info.row.index}
                value={value}
                variantPackUUID={variantPackUUID}
              />
            );
          },
          header: () => <PricingTableHeader columnId={ColumnId.CurrentPrice} />,
          id: ColumnId.CurrentPrice,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CurrentPrice),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original as PricingAccountRow);
            const b = resolveEditedRow(rowB.original as PricingAccountRow);
            return a.priceInMicros - b.priceInMicros;
          },
        },
      ),
      // Price delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.PriceDelta], () =>
            formatCurrency(row.priceDeltaInMicros, currencyCode),
          ),
        {
          cell: (info: PricingCellRenderInput) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              info.getValue(),
            )
              ? emptyDelta
              : resolveDelta(info.row.original.priceDeltaInMicros, (v) =>
                  formatCurrency(v, currencyCode),
                );
            if (info.isAggregateRow) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={direction}
                  value={formattedValue}
                />
              );
            }

            const originalRow = info.row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="priceDeltaInMicros"
                formatValue={(value) => formatCurrency(value, currencyCode)}
                originalArrowDirection={direction}
                originalFormattedValue={formattedValue}
                originalRow={originalRow}
                originalValue={formattedValue}
                testID="original-price-delta"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.PriceDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.PriceDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.PriceDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original as PricingAccountRow);
            const b = resolveEditedRow(rowB.original as PricingAccountRow);
            return a.priceDeltaInMicros - b.priceDeltaInMicros;
          },
        },
      ),
      // Cost delta
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.CostDelta], () =>
            formatCurrency(row.costDeltaInMicros, currencyCode),
          ),
        {
          cell: (info) => {
            const { direction, formattedValue } = isEmptyDisplayValue(
              info.getValue(),
            )
              ? emptyDelta
              : resolveDelta(info.row.original.costDeltaInMicros, (v) =>
                  formatCurrency(v, currencyCode),
                );

            return (
              <PricingTableDeltaCell
                arrowDirection={direction}
                hasInvertedColor
                value={formattedValue}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.CostDelta}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.CostDelta,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.CostDelta),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) =>
            rowA.original.costDeltaInMicros - rowB.original.costDeltaInMicros,
        },
      ),
      // Gross Profit
      columnHelper.accessor(
        (row) =>
          formatOrEmpty(row, [CostChangeValue.GrossProfit], () =>
            formatCurrency(row.grossProfitInMicros, currencyCode),
          ),
        {
          cell: (info: PricingCellRenderInput) => {
            const value = info.getValue();
            if (info.isAggregateRow) {
              return (
                <PricingTableDeltaCell
                  arrowDirection={undefined}
                  hideDeltaAttributes
                  value={value}
                />
              );
            }

            const originalRow = info.row.original as PricingAccountRow;
            return (
              <ItemUpdatedPricingCellContainer
                fieldName="grossProfitInMicros"
                formatValue={(value) => formatCurrency(value, currencyCode)}
                hideDeltaAttributes
                originalFormattedValue={value}
                originalRow={originalRow}
                originalValue={value}
                testID="original-gross-profit"
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.GrossProfit}
              costChangeLookbackMode={costChangeLookbackMode}
              costChangeWeekAnchorDay={costChangeWeekAnchorDay}
              withTooltip
            />
          ),
          id: ColumnId.GrossProfit,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.GrossProfit),
          },
          ...COLUMN_WIDTH.M,
          sortingFn: (rowA, rowB) => {
            const a = resolveEditedRow(rowA.original as PricingAccountRow);
            const b = resolveEditedRow(rowB.original as PricingAccountRow);
            return a.grossProfitInMicros - b.grossProfitInMicros;
          },
        },
      ),
      // Weekly purchase quantities
      columnHelper.accessor(
        (row) =>
          row.weeklyPurchaseQuantities
            .reduce((acc, w) => acc + w.quantity, 0)
            .toString(),
        {
          cell: (info: PricingCellRenderInput) => {
            if (info.isAggregateRow) {
              return null;
            }
            return (
              <WeeklyPurchaseQuantitiesCell
                quantities={info.row.original.weeklyPurchaseQuantities}
              />
            );
          },
          header: () => (
            <PricingTableHeader
              columnId={ColumnId.WeeklyPurchaseQuantities}
              withTooltip
            />
          ),
          id: ColumnId.WeeklyPurchaseQuantities,
          meta: {
            initialPercentWidth: columnWidthPercentage,
            label: getHeaderLabel(ColumnId.WeeklyPurchaseQuantities),
          },
          ...COLUMN_WIDTH.S,
          sortingFn: (rowA, rowB) => {
            const sum = (xs: { quantity: number }[]) =>
              xs.reduce((acc, x) => acc + x.quantity, 0);
            return (
              sum(rowA.original.weeklyPurchaseQuantities) -
              sum(rowB.original.weeklyPurchaseQuantities)
            );
          },
        },
      ),
      // Avg Vol/wk
      columnHelper.accessor((row) => row.quantity.toFixed(1), {
        cell: (info) => (
          <Typography variant="paragraph-md">{info.getValue()}</Typography>
        ),
        header: () => (
          <PricingTableHeader
            columnId={ColumnId.WeeklyVolume}
            costChangeLookbackMode={costChangeLookbackMode}
            costChangeWeekAnchorDay={costChangeWeekAnchorDay}
            withTooltip
          />
        ),
        id: ColumnId.WeeklyVolume,
        meta: {
          initialPercentWidth: columnWidthPercentage,
          label: getHeaderLabel(ColumnId.WeeklyVolume),
        },
        ...COLUMN_WIDTH.XS,
        sortingFn: (rowA, rowB) =>
          rowA.original.quantity - rowB.original.quantity,
      }),
      // CC / Edits / Net GP Impact.
      ...IMPACT_COLUMN_CONFIGS.map(({ columnId, fieldName, getImpact }) =>
        columnHelper.accessor(
          (row) =>
            formatCurrency(
              roundMicrosToNearestWholeCent(getImpact(row)),
              currencyCode,
            ),
          {
            cell: (info: PricingCellRenderInput) => {
              const { direction, formattedValue } = resolveDelta(
                roundMicrosToNearestWholeCent(getImpact(info.row.original)),
                (v) => formatCurrency(v, currencyCode),
              );
              if (info.isAggregateRow || fieldName === null) {
                return (
                  <PricingTableDeltaCell
                    arrowDirection={direction}
                    value={formattedValue}
                  />
                );
              }

              const originalRow = info.row.original as PricingAccountRow;
              return (
                <ItemUpdatedPricingCellContainer
                  fieldName={fieldName}
                  formatValue={(value) =>
                    formatCurrency(
                      roundMicrosToNearestWholeCent(value),
                      currencyCode,
                    )
                  }
                  originalArrowDirection={direction}
                  originalFormattedValue={formattedValue}
                  originalRow={originalRow}
                  originalValue={formattedValue}
                  testID={`original-${columnId}`}
                />
              );
            },
            header: () => (
              <PricingTableHeader columnId={columnId} withTooltip />
            ),
            id: columnId,
            meta: {
              initialPercentWidth: columnWidthPercentage,
              label: getHeaderLabel(columnId),
            },
            ...COLUMN_WIDTH.M,
            sortingFn: (rowA, rowB) => {
              const a = resolveEditedRow(rowA.original as PricingAccountRow);
              const b = resolveEditedRow(rowB.original as PricingAccountRow);
              return getImpact(a) - getImpact(b);
            },
          },
        ),
      ),
      ...(dataToTransform[0]?.price_edit_display_headers ?? []).map(
        (header) => {
          const label = getTranslationValue(header.translation);
          return columnHelper.accessor(
            (row) =>
              (row as PricingAccountRow).supplierDisplayInfo?.[header.key]
                ?.display ?? '',
            {
              cell: (info) => (
                <Typography variant="paragraph-md">
                  {info.getValue()}
                </Typography>
              ),
              header: () => (
                <Typography
                  color={theme.sk.color.text.tertiary}
                  variant="paragraph-sm-600"
                >
                  {label}
                </Typography>
              ),
              id: header.key,
              meta: {
                defaultHidden: true,
                initialPercentWidth: columnWidthPercentage,
                label,
              },
              ...COLUMN_WIDTH.M,
              sortingFn: (rowA, rowB) => {
                const a = (rowA.original as PricingAccountRow)
                  .supplierDisplayInfo?.[header.key]?.raw;
                const b = (rowB.original as PricingAccountRow)
                  .supplierDisplayInfo?.[header.key]?.raw;

                if (a === null || a === undefined) {
                  if (b === null || b === undefined) {
                    return 0;
                  }
                  return -1;
                }
                if (b === null || b === undefined) {
                  return 1;
                }

                if (typeof a === 'number' && typeof b === 'number') {
                  return a - b;
                }

                return String(a).localeCompare(String(b));
              },
            },
          );
        },
      ),
    ],
    [
      columnWidthPercentage,
      costChangeLookbackMode,
      costChangeWeekAnchorDay,
      currencyCode,
      dataToTransform,
      hasBookmarkingAccess,
      isHistoricalPricingContextEnabled,
      isPeerRangeEnabled,
      navigation,
      resolveEditedRow,
      selectedAccountUUIDs,
      styles,
      theme,
      toggleAccountSelection,
      variantPackUUID,
      editScope,
    ],
  );

  const { columnOrder, columnVisibility } = useConfigureColumns(
    ConfigurationDomain.Pricing,
    TableName.PricingItem,
    columns,
  );

  const restaurantEditCounts = useMemo(() => {
    return pricingEdits.reduce<Record<string, number>>((acc, edit) => {
      if (edit.variantPackUUID === variantPackUUID) {
        acc[edit.restaurantUUID] = (acc[edit.restaurantUUID] ?? 0) + 1;
      }
      return acc;
    }, {});
  }, [pricingEdits, variantPackUUID]);

  const bookmarks = usePricingBookmarksStore((state) => state.bookmarks);
  const bookmarkedByRestaurant = useMemo(() => {
    return transformedData.reduce<Record<string, boolean>>((acc, row) => {
      acc[row.restaurantUUID] =
        bookmarkKey(variantPackUUID, row.restaurantUUID) in bookmarks;
      return acc;
    }, {});
  }, [bookmarks, transformedData, variantPackUUID]);

  const rangeColumnConfigs = useMemo<
    Record<string, PricingRangeColumnConfig<PricingAccountRow>>
  >(() => {
    const currencyAffix = getCurrencySymbol(currencyCode);

    const impactRangeEntries: [
      string,
      PricingRangeColumnConfig<PricingAccountRow>,
    ][] = IMPACT_COLUMN_CONFIGS.map(({ columnId, getImpact }) => [
      columnId,
      {
        accessor: (row) => getImpact(row) / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(columnId),
        unit: PricingFilterUnit.Currency,
      },
    ]);

    return {
      [ColumnId.CurrentGrossMargin]: {
        accessor: (row) => row.grossMarginPercentage,
        affix: '%',
        title: getHeaderLabel(ColumnId.CurrentGrossMargin),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.GrossMarginPercentageDelta]: {
        accessor: (row) => row.grossMarginPercentageDelta,
        affix: '%',
        title: getHeaderLabel(ColumnId.GrossMarginPercentageDelta),
        unit: PricingFilterUnit.Percent,
      },
      [ColumnId.CurrentPrice]: {
        accessor: (row) => row.priceInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.CurrentPrice),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.PriceDelta]: {
        accessor: (row) => row.priceDeltaInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        filterPillMetric: (row) =>
          percentOfPrevious(row.priceDeltaInMicros, getLastPriceInMicros(row)),
        title: getHeaderLabel(ColumnId.PriceDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.CostDelta]: {
        accessor: (row) => row.costDeltaInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        filterPillMetric: (row) =>
          percentOfPrevious(row.costDeltaInMicros, getLastCostInMicros(row)),
        title: getHeaderLabel(ColumnId.CostDelta),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.GrossProfit]: {
        accessor: (row) => row.grossProfitInMicros / MICRO_CONVERSION,
        affix: currencyAffix,
        title: getHeaderLabel(ColumnId.GrossProfit),
        unit: PricingFilterUnit.Currency,
      },
      [ColumnId.WeeklyVolume]: {
        accessor: (row) => row.quantity,
        affix: 'u',
        title: getHeaderLabel(ColumnId.WeeklyVolume),
        unit: PricingFilterUnit.Unit,
      },
      ...Object.fromEntries(impactRangeEntries),
    };
  }, [currencyCode]);

  // Declared order, never `columnOrder` — rearranging the table must not move
  // the filters. UOM excluded: every row here is the same variant pack, so a
  // UOM (and Category) pill would be a single degenerate option.
  const filterColumnIds = useMemo(
    () =>
      columns
        .map((column) => column.id)
        .filter((id): id is string => Boolean(id) && id !== ColumnId.UOM),
    [columns],
  );

  const sections = useMemo(
    () =>
      buildPricingFilterSections({
        bookmarked: hasBookmarkingAccess
          ? {
              bookmarkedByRow: bookmarkedByRestaurant,
              getRowId: (row) => row.restaurantUUID,
            }
          : undefined,
        categoryOptions: [],
        columnIds: filterColumnIds,
        editCounts: {
          counts: restaurantEditCounts,
          getRowId: (row) => row.restaurantUUID,
        },
        peerAverage: isPeerRangeEnabled
          ? {
              getCurrentGrossMargin: (row) => row.grossMarginPercentage,
              getPeerRange: (row) => row.peerRange,
            }
          : undefined,
        rangeColumnConfigs,
        rows: mergedData,
        timezone,
      }),
    [
      bookmarkedByRestaurant,
      filterColumnIds,
      hasBookmarkingAccess,
      isPeerRangeEnabled,
      restaurantEditCounts,
      rangeColumnConfigs,
      mergedData,
      timezone,
    ],
  );

  const filters = usePricingFilters(
    PricingFilterScope.ItemCostChanges,
    sections,
  );
  const panel = usePricingFilterPanel();

  const handleFiltersUrlUpdate = useCallback(
    (params: Record<string, string | undefined>) =>
      navigation.setParams(params),
    [navigation],
  );

  usePricingFiltersUrlSync({
    hydrate: filters.hydrate,
    onUrlUpdate: handleFiltersUrlUpdate,
    routeParams: route.params,
    sections,
    selections: { pills: filters.pills, ranges: filters.ranges },
  });

  // Drop filters whose column is gone, so they can't silently keep shrinking
  // the table. Depends on the store action, not `filters`, which would loop.
  useEffect(() => {
    filters.pruneSections(sections.map((section) => section.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sections, filters.pruneSections]);

  // Store slices, not `filters`: that object is new every render, so the
  // Table would get a new array on every keystroke.
  const { pills: filterPills, ranges: filterRanges } = filters;
  // Filters judge the post-edit rows (mergedData), but the table itself keeps
  // rendering the unedited baseline rows: cells like ItemInputCell diff
  // against that baseline to compute their own edit overlay.
  const filteredRows = useMemo(() => {
    const passingRestaurantUUIDs = new Set(
      applyPricingFilters(
        mergedData,
        { pills: filterPills, ranges: filterRanges },
        sections,
      ).map((row) => row.restaurantUUID),
    );
    return transformedData.filter((row) =>
      passingRestaurantUUIDs.has(row.restaurantUUID),
    );
  }, [mergedData, transformedData, filterPills, filterRanges, sections]);

  const handleClearEmptyState = useCallback(() => {
    if (debouncedSearchText.length > 0) {
      clearSearch();
    } else {
      filters.clearAll();
    }
  }, [debouncedSearchText, clearSearch, filters]);

  const LoadingSkeleton = () => (
    <View
      style={styles.loadingContent}
      testID="pricing-management-loading-skeleton"
    >
      <SearchSkeleton />
      <PricingTableSkeleton />
    </View>
  );

  const renderHeaderRow = useCallback<
    RenderHeaderRowCallback<PricingItemManagementTableRow>
  >(
    ({ headerGroup }) => (
      <React.Fragment key={headerGroup.id}>
        <PricingTableHeaderCells
          headerGroup={headerGroup}
          isLeftAlignedColumn={isLeftAlignedColumn}
          sorting={sorting}
        />
        {aggregateData ? (
          <PricingAggregateRow
            data={aggregateData}
            editsCount={activeEditsByVariantPackCount}
            headers={headerGroup.headers}
            isDeltaColumn={isDeltaColumn}
            isLeftAlignedColumn={isLeftAlignedColumn}
            testID="pricing-aggregate-header-row"
          />
        ) : null}
      </React.Fragment>
    ),
    [activeEditsByVariantPackCount, aggregateData, sorting],
  );

  const renderRow = useCallback<
    RenderRowCallback<PricingItemManagementTableRow>
  >(
    ({ index, row }) => {
      const originalRow = row.original as PricingAccountRow;
      const hasChanges = pricingEdits.some(
        (edit) =>
          edit.restaurantUUID === originalRow.restaurantUUID &&
          edit.variantPackUUID === originalRow.variantPackUUID,
      );
      return (
        <PricingItemDetailsTableRow
          columnOrder={columnOrder}
          columnVisibility={columnVisibility}
          hasChanges={hasChanges}
          index={index}
          isSelected={selectedAccountUUIDs.has(originalRow.restaurantUUID)}
          key={row.id}
          row={row}
        />
      );
    },
    [columnOrder, columnVisibility, pricingEdits, selectedAccountUUIDs],
  );

  // Fresh objects here would churn the table context and FlashList each render.
  const cellPadding = useMemo(
    () => [
      // Checkbox column keeps the wider padding so its
      // right-aligned checkbox isn't clipped.
      theme.sk.spacing.spaceMedium,
      ...Array(columns.length - 1).fill(theme.sk.spacing.spaceSmall),
    ],
    [columns.length, theme.sk.spacing.spaceMedium, theme.sk.spacing.spaceSmall],
  );
  const bodyContainerStyle = useMemo(
    () => ({
      // Enable last table row to be visible above bulk edit bar
      paddingBottom: isBulkEditBarVisible
        ? BAR_CONTROL_HEIGHT + 2 * theme.sk.spacing.spaceLarge
        : theme.sk.spacing.spaceLarge,
    }),
    [isBulkEditBarVisible, theme.sk.spacing.spaceLarge],
  );

  return (
    <SalesHubShellTemplate>
      <AppPageLayout
        actions={
          <View
            style={[
              styles.topBarActions,
              panel.isOpen && styles.actionsWithPanelOpen,
            ]}
          >
            {hasUndoHistory ? <PricingUndoButton onPress={handleUndo} /> : null}
            {hasPriceEdits ? (
              <PricingReviewButton
                onPress={() =>
                  navigation.navigate(
                    SALES_PORTAL_ROUTES.PricingManagementReview,
                  )
                }
              />
            ) : null}
          </View>
        }
        leadingActions={
          <PricingBreadcrumbs
            parentRoute={SALES_PORTAL_ROUTES.PricingManagement}
            secondaryBreadcrumb={i18n.t('pricing.cost_changes.sidenav_title')}
            tertiaryBreadcrumb={aggregateData?.variantPackId ?? ''}
          />
        }
        overlay={
          panel.isOpen ? (
            <PricingFilterPanel
              filters={filters}
              onClose={panel.close}
              sections={sections}
            />
          ) : null
        }
        scroll={false}
      >
        <View
          style={[
            styles.container,
            panel.isOpen && styles.containerWithPanelOpen,
          ]}
        >
          {loading || timezoneLoading ? (
            <LoadingSkeleton />
          ) : error ? (
            <EmptyState
              bottomElement={
                <Button
                  accessibilityLabel={i18n.t('sales_portal.error.retry')}
                  onPress={async () => await refetch()}
                  size={ButtonSize.Medium}
                  variant={ButtonVariant.Secondary}
                  width={120}
                >
                  {i18n.t('sales_portal.error.retry')}
                </Button>
              }
              description={i18n.t('pricing.error.table.description')}
              iconElement={
                <CircleWarningIcon stroke={theme.sk.color.icon.primary} />
              }
              title={i18n.t('pricing.error.table.title')}
            />
          ) : (
            <Suspense fallback={<LoadingSkeleton />}>
              <PricingSearchBar
                trailing={
                  <>
                    <PricingFilterButton
                      activeCount={filters.activeCount}
                      onPress={panel.open}
                    />
                    <ConfigureColumnsButton
                      columns={columns}
                      domain={ConfigurationDomain.Pricing}
                      table={TableName.PricingItem}
                    />
                  </>
                }
              >
                <SearchInput
                  onSearchChange={handleSearchChange}
                  placeholderText={i18n.t('pricing.search_placeholder')}
                  ref={searchInputRef}
                  searchText={searchText}
                />
              </PricingSearchBar>
              <View onLayout={handleTableLayout} style={styles.tableWrapper}>
                <Table
                  bodyContainerStyle={bodyContainerStyle}
                  cellPadding={cellPadding}
                  columns={columns}
                  data={filteredRows}
                  emptyComponent={
                    <PricingFilterEmptyState
                      baseEmptyComponent={<PricingTableEmptyComponent />}
                      hasActiveFilters={filters.hasActiveFilters}
                      onClear={handleClearEmptyState}
                      searchText={debouncedSearchText}
                    />
                  }
                  estimatedRowHeight={PRICING_TABLE_LAYOUT.ESTIMATED_ROW_HEIGHT}
                  fillAvailableWidth
                  globalFilterFn={pricingItemGlobalFilterFn}
                  headerPadding={theme.sk.spacing.spaceSmall}
                  initialState={{
                    columnPinning: {
                      left: [ColumnId.Checkbox, ColumnId.Name],
                    },
                  }}
                  isActive
                  layoutWidth={tableLayoutWidth ?? undefined}
                  onFilteredRowsChange={onFilteredRowsChange}
                  onSortingChange={onSortingChange}
                  renderHeaderRow={renderHeaderRow}
                  renderRow={renderRow}
                  sectionHeader={
                    filteredRows.length > 0 ? (
                      <PricingSelectAllRow
                        disabled={isAllLocked}
                        isAllSelected={isAllSelected}
                        isPartiallySelected={isPartiallySelected}
                        label={i18n.t(
                          filteredCount === 1
                            ? 'pricing.table.select_all.singular'
                            : 'pricing.table.select_all.plural',
                          { count: filteredCount },
                        )}
                        onToggleAll={toggleAllVisible}
                      />
                    ) : null
                  }
                  state={{
                    columnOrder,
                    columnVisibility,
                    globalFilter: debouncedSearchText,
                    sorting,
                  }}
                />
                {isBulkEditBarVisible ? (
                  <PricingBulkEditBottomBar
                    canApply={canApplyBulkInput}
                    editField={bulkEditField}
                    editMode={bulkEditMode}
                    isBookmarkNoteOpen={isBookmarkNoteOpen}
                    onApply={handleApplyBulkInput}
                    onEditFieldChange={setBulkEditField}
                    onEditModeChange={setBulkEditMode}
                    onInputValueChange={setBulkInputValue}
                    onSaveBookmarkNote={onSaveBookmarkNote}
                    quickActions={bulkQuickActions}
                    selectedCount={selectedCount}
                    selectedCountLabel={i18n.t(
                      selectedCount === 1
                        ? 'pricing.bulk_edit.customer_count.singular'
                        : 'pricing.bulk_edit.customer_count.plural',
                      { count: selectedCount },
                    )}
                    value={bulkInputValue}
                  />
                ) : null}
              </View>
            </Suspense>
          )}
        </View>
      </AppPageLayout>
    </SalesHubShellTemplate>
  );
};

const useStyles = createStyles((theme) => ({
  actionsWithPanelOpen: {
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  checkbox: {
    paddingHorizontal: theme.sk.spacing.spaceSMedium,
    paddingVertical: 14,
  },
  checkboxContainer: {
    alignItems: 'flex-end',
    justifyContent: 'flex-start',
    // Use negative margin to increase interaction target
    marginHorizontal:
      -theme.sk.spacing.spaceLarge - theme.sk.spacing.spaceXsmall,
    marginVertical: -theme.sk.spacing.spaceSMedium,
  },
  container: {
    flex: 1,
    gap: theme.sk.spacing.spaceMedium,
    paddingTop: theme.sk.spacing.spaceMedium,
  },
  containerWithPanelOpen: {
    minWidth: PRICING_CONTENT_MIN_WIDTH + PRICING_FILTER_PANEL_WIDTH,
    paddingRight: PRICING_FILTER_PANEL_WIDTH,
  },
  loadingContent: {
    flex: 1,
    gap: 42,
  },
  tableWrapper: {
    flex: 1,
    position: 'relative',
  },
  topBarActions: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
